import { Dispatch, SetStateAction, useContext, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import { Alert, AlertTitle, Button, CircularProgress, DialogActions, DialogContent, Grid, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import _ from 'lodash'

import DialogWithTitle from '../../../../../components/dialog-with-title'
import { AppContext, useHost } from '../../../../../providers/app-provider'
import Api from '../../../../../utils/api'
import useStyles from '../index.styles'

interface IProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  folderId: number
  folderName: string
}

interface IOptOutFailedStates {
  cantWriteUsers: boolean
  cantWriteFolder: boolean
  cantMoveFolder: boolean
  noUsersFound: boolean
  subscriptionFound: boolean
  weatherNodeNotFound: boolean
}

interface IOptOutNode {
  id: number
  vanity: string
  nodeType: string
}

const initialFailedState: IOptOutFailedStates = {
  cantWriteUsers: false,
  cantWriteFolder: false,
  cantMoveFolder: false,
  noUsersFound: false,
  subscriptionFound: false,
  weatherNodeNotFound: false,
}

const ConfirmOptOutModal = (props: IProps) => {
  const { open, setOpen, folderId, folderName } = props

  const classes = useStyles
  const theme = useTheme()
  const auth = useAuth()
  const host = useHost()
  const { refreshPage } = useContext(AppContext)

  const [effectiveDate, setEffectiveDate] = useState<Date | null>(new Date())

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [optOutInitiated, setOptOutInitiated] = useState<boolean>(false)

  const [genericError, setGenericError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [failedOptOutStates, setFailedOptOutStates] = useState<IOptOutFailedStates>(initialFailedState)

  const steps = [{ label: 'Update folder status' }, { label: 'Remove weather node' }, { label: 'Move folder to opt outs' }, { label: 'Remove/delete users' }]

  const handleSubmit = async () => {
    if (!host || !auth.user?.access_token) return
    setIsSubmitting(true)
    Api.post(`${host}/api/v2/central/folders/${folderId}/opt-out`, { effectiveAt: effectiveDate }).then((res) => {
      if (res.ok) {
        if (!res.body.deactivatedNodes?.some((node: IOptOutNode) => node.nodeType === 'mweather')) {
          setFailedOptOutStates((prev) => ({ ...prev, weatherNodeNotFound: true }))
        }

        if (!_.isEmpty(res.body.subscription)) {
          setFailedOptOutStates((prev) => ({ ...prev, subscriptionFound: true }))
        }

        if ((!res.body.removedUsers || res.body.removedUsers.length === 0) && (!res.body.deletedUsers || res.body.deletedUsers.length === 0)) {
          setFailedOptOutStates((prev) => ({ ...prev, noUsersFound: true }))
        }

        if (res.body.folderDeactivated) {
          setSuccess(true)
        }

        if (
          (!res.body.optOutsFolderId || res.body.optOutsFolderId === '') &&
          !res.body.deactivatedNodes?.some((node: IOptOutNode) => node.nodeType === 'whiskerting')
        ) {
          setSuccess(false)
          setFailedOptOutStates((prev) => ({ ...prev, cantMoveFolder: true }))
        }
      } else if (res.status === 403) {
        if (res.body.message === 'permission denied: Write Users') {
          setFailedOptOutStates((prev) => ({ ...prev, cantWriteUsers: true }))
        }
        if (res.body.message === 'permission denied: Write Folders') {
          setFailedOptOutStates((prev) => ({ ...prev, cantWriteFolder: true }))
        }
      } else {
        setGenericError('Something went wrong')
      }

      setOptOutInitiated(true)
      setIsSubmitting(false)
    })
  }

  const handleDone = () => {
    setOpen(false)
    setOptOutInitiated(false)
    setFailedOptOutStates(initialFailedState)
    setSuccess(false)
    setGenericError(null)

    if (success) refreshPage()
  }

  return (
    <DialogWithTitle setOpen={setOpen} open={open} title={!optOutInitiated ? 'Confirm Opt Out' : 'Processing Opt Out'} sx={classes.dialog}>
      <DialogContent>
        {!optOutInitiated ? (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="subtitle2">You have selected this folder to opt out:</Typography>
              <Typography variant="body1">{folderName}</Typography>
            </Grid>

            <Grid size={12}>
              <Alert severity="warning">
                <AlertTitle>Notice:</AlertTitle>
                Please process all returns before initiating the opt-out. All remaining devices will be deactivated.
              </Alert>
            </Grid>

            <Grid size={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker label="Effective Date" value={effectiveDate} onChange={(newValue) => setEffectiveDate(newValue)} disableFuture={true} />
              </LocalizationProvider>
            </Grid>
          </Grid>
        ) : (
          <Grid container={true} spacing={2}>
            <Stepper nonLinear={true} orientation="vertical" sx={classes.optOutStepper}>
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel
                    icon={
                      isSubmitting ? (
                        <FiberManualRecordOutlinedIcon fontSize="small" />
                      ) : (
                        <FiberManualRecordIcon
                          fontSize="small"
                          sx={{
                            ...classes.greenStep(theme),
                            ...(success !== true ||
                            (step.label === 'Remove weather node' && failedOptOutStates.weatherNodeNotFound === true) ||
                            (step.label === 'Remove/delete users' && failedOptOutStates.noUsersFound === true)
                              ? classes.greyStep
                              : {}),
                            ...(step.label === 'Move folder to opt outs' && failedOptOutStates.cantMoveFolder === true ? classes.redStep(theme) : {}),
                          }}
                        />
                      )
                    }
                  >
                    <Typography
                      variant="overline"
                      color="textPrimary"
                      sx={{
                        ...(success !== true ||
                        (step.label === 'Remove weather node' && failedOptOutStates.weatherNodeNotFound === true) ||
                        (step.label === 'Remove/delete users' && failedOptOutStates.noUsersFound === true)
                          ? classes.greyStep
                          : {}),
                      }}
                    >
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {isSubmitting && (
              <Grid size={12}>
                <Alert severity="info" icon={false}>
                  <CircularProgress size={14} sx={classes.processingSpinner} />
                  Processing opt out
                </Alert>
              </Grid>
            )}

            {genericError && (
              <Grid size={12}>
                <Alert severity="error">{genericError}</Alert>
              </Grid>
            )}

            {failedOptOutStates.cantWriteUsers && (
              <Grid size={12}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  Users could not be removed or deleted. Please manually remove users.
                </Alert>
              </Grid>
            )}

            {failedOptOutStates.cantWriteFolder && (
              <Grid size={12}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  Folder status could not be updated. Please manually update folder status.
                </Alert>
              </Grid>
            )}

            {failedOptOutStates.subscriptionFound && (
              <Grid size={12}>
                <Alert severity="warning">Subscription found, please ensure the subscription is cancelled.</Alert>
              </Grid>
            )}

            {failedOptOutStates.noUsersFound && (
              <Grid size={12}>
                <Alert severity="info">No users found on folder.</Alert>
              </Grid>
            )}

            {failedOptOutStates.weatherNodeNotFound && (
              <Grid size={12}>
                <Alert severity="info">Weather node not found.</Alert>
              </Grid>
            )}

            {failedOptOutStates.cantMoveFolder && (
              <Grid size={12}>
                <Alert severity="error">Folder could not be moved. Please manually move folder.</Alert>
              </Grid>
            )}

            {success && (
              <Grid size={12}>
                <Alert severity="success">Folder successfully opted out </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogContent>
        <DialogActions>
          {!optOutInitiated ? (
            <>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button variant="contained" onClick={handleSubmit} disabled={!effectiveDate} endIcon={<ArrowForwardIosIcon sx={classes.initiateIcon} />}>
                Initiate
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleDone} disabled={isSubmitting}>
              Done
            </Button>
          )}
        </DialogActions>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default ConfirmOptOutModal
